import { useMemo, useRef } from 'react';
import { useForm } from '@tanstack/react-form';
import { useTranslation } from 'react-i18next';

import { useUpdateVenuaPlannerTerm } from '@/api/generated/venua-planner-planning/venua-planner-planning';
import type { GetTermDetailResponse } from '@/api/generated/schemas';
import { mapServerValidationErrors, type ServerFieldMap } from '@/shared/forms';
import {
  buildTermDetailFormSchema,
  fromGetTermDetailResponse,
  type TermDetailFormValues,
} from './termDetailFormSchema';
import { toUpdateTermRequestBody } from './toTermDetailRequest';

/** Mapování názvů polí z BE validace na cesty polí ve formuláři. */
const SERVER_FIELD_MAP: ServerFieldMap = {
  Code: 'code',
};

interface UseTermDetailFormOptions {
  productId: string;
  serieId: string;
  termId: string;
  /** Detail termínu pro výchozí hodnoty (form dostane správné defaulty hned, žádný `form.reset`). */
  detail: GetTermDetailResponse;
  /** Voláno po úspěšném uložení (invalidace + toast). NEzavírá okno – o zavření rozhoduje volající. */
  onSaved: () => void;
  /** Voláno při chybě, kterou se nepodařilo namapovat na konkrétní pole. */
  onError?: (error: unknown) => void;
}

/**
 * Zapouzdřuje formulářovou logiku editace termínu – TanStack Form se Zod validací, `UpdateTerm` mutace
 * a promítnutí serverových validačních chyb do polí (stejný vzor jako editace seriálu).
 *
 * Vrací `submit()` vracející `Promise<boolean>` (úspěch uložení) pro „Uložit & schválit“, kde se navazující akce
 * spustí jen po úspěšném uložení.
 */
export function useTermDetailForm({
  productId,
  serieId,
  termId,
  detail,
  onSaved,
  onError,
}: UseTermDetailFormOptions) {
  const { t } = useTranslation('venua-planner');
  const schema = useMemo(() => buildTermDetailFormSchema(t), [t]);
  const updateMutation = useUpdateVenuaPlannerTerm();
  const lastSubmitOkRef = useRef(false);

  const form = useForm({
    defaultValues: fromGetTermDetailResponse(detail),
    validators: { onChange: schema },
    onSubmit: async ({ value, formApi }) => {
      try {
        await updateMutation.mutateAsync({
          productId,
          serieId,
          termGroupId: termId,
          data: toUpdateTermRequestBody(value),
        });
        lastSubmitOkRef.current = true;
        onSaved();
      } catch (error) {
        const mapped = mapServerValidationErrors(formApi, error, SERVER_FIELD_MAP);
        if (!mapped) onError?.(error);
      }
    },
  });

  /** Spustí validaci + submit a vrátí, zda uložení proběhlo úspěšně. */
  const submit = async (): Promise<boolean> => {
    lastSubmitOkRef.current = false;
    await form.handleSubmit();
    return lastSubmitOkRef.current;
  };

  return { form, submit, isSubmitting: updateMutation.isPending };
}

/** Typ TanStack Form instance editace termínu (sdílený do `TermBasicsForm`). */
export type TermDetailFormApi = ReturnType<typeof useTermDetailForm>['form'];

export type { TermDetailFormValues };
